/**
 * Component displaying a paginated list of change records.
 */

import { useState, useEffect } from 'react';
import { Empty, Pagination, Spin, Alert } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import ChangeRecordItem from './ChangeRecordItem';
import { getVersionDetail } from '@/api/versions';
import type { RequirementChangeRecord, PaginatedChangeRecords } from '@/types';
import { useT } from '@/i18n';

interface ChangeRecordListProps {
  projectId: number;
  versionId: number;
  changeType?: string; // Optional filter by change type
}

const ChangeRecordList: React.FC<ChangeRecordListProps> = ({
  projectId,
  versionId,
  changeType,
}) => {
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<RequirementChangeRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchRecords = async (page: number = 1, pageSize: number = 20) => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number> = {
        page,
        page_size: pageSize,
      };
      if (changeType) {
        params.change_type = changeType;
      }

      const version = await getVersionDetail(projectId, versionId, params);

      // Check if change_records is paginated format
      const changeRecords = version.change_records;
      if (changeRecords && typeof changeRecords === 'object' && 'results' in changeRecords) {
        const paginatedRecords = changeRecords as PaginatedChangeRecords;
        setRecords(paginatedRecords.results);
        setPagination({
          current: paginatedRecords.page,
          pageSize: paginatedRecords.page_size,
          total: paginatedRecords.count,
        });
      } else {
        // Fallback for array format (shouldn't happen in detail view)
        setRecords(Array.isArray(changeRecords) ? changeRecords : []);
        setPagination({
          current: 1,
          pageSize: 20,
          total: Array.isArray(changeRecords) ? changeRecords.length : 0,
        });
      }
    } catch (err: unknown) {
      console.error('Failed to fetch change records:', err);
      setError(err instanceof Error ? err.message : t('加载变更记录失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(pagination.current, pagination.pageSize);
  }, [projectId, versionId, changeType, t]);

  const handlePageChange = (page: number, pageSize?: number) => {
    fetchRecords(page, pageSize || pagination.pageSize);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '30px 0' }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        title={t('加载失败')}
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (records.length === 0) {
    return (
      <Empty
        image={<FileSearchOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
        description={t('暂无变更记录')}
        style={{ padding: '30px 0' }}
      />
    );
  }

  return (
    <div>
      {records.map((record, index) => (
        <ChangeRecordItem
          key={`${record.requirement_id}-${record.changed_at}-${index}`}
          record={record}
        />
      ))}

      {pagination.total > pagination.pageSize && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger
            showTotal={(total) => t('共 {total} 条变更记录', { total })}
          />
        </div>
      )}
    </div>
  );
};

export default ChangeRecordList;
